/**
 * Watchlist Management Page — full feature set
 *
 * Features:
 *   1. Symbol Screener     — search Alpaca's asset universe, filter by exchange
 *   2. Bulk CSV Import     — upload/paste CSV list, validate and add in batch
 *   3. Multiple Watchlists — create named lists, filter the table by list
 *   4. Auto-Validation     — re-validate all active symbols against Alpaca
 *   5. Performance Ranking — sort table by any column
 *   6. Hot-Reload (engine) — watchlist changes propagate to DataCollector
 *                            within ~1 minute via DB timestamp signal;
 *                            no Railway restart required
 *
 * Hot-reload mechanism:
 *   add/remove/bulk-add endpoints bump watchlist_watchlists.updated_at.
 *   DataCollector._check_watchlist_changes() reads this each cycle and
 *   reinitialises rotation groups automatically when the timestamp changed.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Search, Plus, Trash2, RefreshCw, CheckCircle, XCircle,
  TrendingUp, BarChart2, List,
  Upload, ShieldCheck, ChevronUp, ChevronDown, Folder, FolderPlus, Link
} from 'lucide-react'
import { api } from '../lib/api'
import { fmtShort, timeAgo } from '../lib/utils'

// ── Sector options (matches system classification) ────────────────────────
const SECTORS = [
  'Technology', 'Consumer Discretionary', 'Consumer Defensive',
  'Healthcare', 'Financial Services', 'Industrials', 'Energy',
  'ETF', 'Commodities', 'Real Estate', 'Communication Services', 'REIT', 'Other'
]
const EXCHANGES = ['', 'NASDAQ', 'NYSE', 'AMEX', 'OTC']

// ── Shared style ──────────────────────────────────────────────────────────
const mono = { fontFamily: 'JetBrains Mono, monospace' }
const darkInput = {
  background: '#0f172a', border: '1px solid #374151',
  color: '#94a3b8', borderRadius: '0.5rem',
  padding: '0.4rem 0.75rem', outline: 'none', ...mono, fontSize: '0.75rem',
}
const darkSelect = { ...darkInput, cursor: 'pointer' }

// ── Sortable column header ────────────────────────────────────────────────
function SortHeader({ label, field, sort, onSort, right }) {
  const active = sort.field === field
  return (
    <th className={`px-4 py-3 cursor-pointer select-none ${right ? 'text-right' : 'text-left'}`}
      style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.1em', color: active ? '#94a3b8' : '#4b5563', fontWeight: 600, textTransform: 'uppercase' }}
      onClick={() => onSort(field)}>
      <span className="inline-flex items-center gap-1">
        {!right && label}
        {active ? (sort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ChevronDown className="h-3 w-3 opacity-20" />}
        {right && label}
      </span>
    </th>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function Watchlist() {

  // ── Watchlist data state ────────────────────────────────────────────────
  const [symbols,    setSymbols]    = useState([])
  const [watchlists, setWatchlists] = useState([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState(null)

  // ── Active tab: 'add' | 'screener' | 'bulk' | 'lists' ─────────────────
  const [tab, setTab] = useState('add')

  // ── Table state ─────────────────────────────────────────────────────────
  const [filterText,   setFilterText]   = useState('')
  const [filterList,   setFilterList]   = useState('all')
  const [showInactive, setShowInactive] = useState(false)
  const [sort,         setSort]         = useState({ field: 'symbol', dir: 'asc' })
  const [removing,     setRemoving]     = useState(null)
  const [validatingAll, setValidatingAll] = useState(false)

  // ── Add-symbol state ────────────────────────────────────────────────────
  const [addInput,     setAddInput]     = useState('')
  const [addSector,    setAddSector]    = useState('Technology')
  const [addWatchlist, setAddWatchlist] = useState('main_watchlist')
  const [validating,   setValidating]   = useState(false)
  const [validation,   setValidation]   = useState(null)
  const [adding,       setAdding]       = useState(false)
  const [addResult,    setAddResult]    = useState(null)

  // ── Screener state ──────────────────────────────────────────────────────
  const [screenerQ,   setScreenerQ]   = useState('')
  const [screenerExch, setScreenerExch] = useState('')
  const [screenerRes, setScreenerRes] = useState([])
  const [screenerLoading, setScreenerLoading] = useState(false)

  // ── Bulk import state ──────────────────────────────────────────────────
  const [bulkText,   setBulkText]   = useState('')
  const [bulkSector, setBulkSector] = useState('Technology')
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkResult,  setBulkResult]  = useState(null)
  const fileRef = useRef(null)

  // ── TradingView import state ────────────────────────────────────────────
  const [tvUrl,             setTvUrl]             = useState('https://www.tradingview.com/watchlists/197983740/')
  const [tvFetching,        setTvFetching]        = useState(false)
  const [tvPreview,         setTvPreview]         = useState(null)   // { watchlist_name, new, already, to_disable, total }
  const [tvSyncing,         setTvSyncing]         = useState(false)
  const [tvResult,          setTvResult]          = useState(null)
  const [tvDefaultSector,   setTvDefaultSector]   = useState('Technology')
  const [tvSectorOverrides, setTvSectorOverrides] = useState({})  // { SYMBOL: 'Sector' }
  const [tvDisableMissing,   setTvDisableMissing]   = useState(false)
  const [tvDetectedSectors,  setTvDetectedSectors]  = useState({})  // { SYMBOL: auto-detected sector }
  const [tvDetecting,        setTvDetecting]        = useState(false)

  // ── Create watchlist state ─────────────────────────────────────────────
  const [newListName, setNewListName] = useState('')
  const [newListDesc, setNewListDesc] = useState('')
  const [creatingList, setCreatingList] = useState(false)

  // ── Load data ──────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [wl, wls] = await Promise.allSettled([api.watchlist(), api.watchlists()])
      if (wl.status  === 'fulfilled' && wl.value.success)  setSymbols(wl.value.symbols || [])
      if (wls.status === 'fulfilled' && wls.value.success) setWatchlists(wls.value.watchlists || [])
      if (wl.status === 'rejected' || wls.status === 'rejected') setError('Unable to load watchlist data')
    } catch { setError('Unable to reach backend') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])
  useEffect(() => {
    const id = setInterval(fetchAll, 300_000)   // 5-minute auto-refresh
    return () => clearInterval(id)
  }, [fetchAll])

  // ── Sort helper ────────────────────────────────────────────────────────
  const handleSort = (field) => {
    setSort(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }))
  }

  // ── Derived / filtered data ────────────────────────────────────────────
  const filtered = symbols
    .filter(s => {
      if (!showInactive && !s.is_active) return false
      if (filterList !== 'all' && s.watchlist_name !== filterList) return false
      if (filterText) {
        const q = filterText.toLowerCase()
        return s.symbol.toLowerCase().includes(q) || (s.sector || '').toLowerCase().includes(q)
      }
      return true
    })
    .sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1
      const av = a[sort.field] ?? ''
      const bv = b[sort.field] ?? ''
      return av < bv ? -dir : av > bv ? dir : 0
    })

  const activeCount  = symbols.filter(s => s.is_active).length
  const sectorCount  = new Set(symbols.filter(s => s.is_active).map(s => s.sector)).size

  // ── Add symbol ─────────────────────────────────────────────────────────
  const handleValidate = async () => {
    const sym = addInput.trim().toUpperCase()
    if (!sym) return
    setValidating(true); setValidation(null); setAddResult(null)
    try {
      const res = await api.validateSymbol(sym)
      setValidation(res)
    } catch {
      setValidation({ success: false, error: 'Validation failed — backend unreachable' })
    } finally { setValidating(false) }
  }

  const handleAdd = async () => {
    if (!validation?.tradeable) return
    setAdding(true); setAddResult(null)
    try {
      const res = await api.addSymbol(validation.symbol, addSector, addWatchlist)
      setAddResult(res)
      if (res.success) {
        setAddInput(''); setValidation(null)
        await fetchAll()
        setTimeout(fetchAll, 4000)
      }
    } catch { setAddResult({ success: false, error: 'Request failed' }) }
    finally { setAdding(false) }
  }

  // ── Screener ───────────────────────────────────────────────────────────
  const handleScreener = async () => {
    if (!screenerQ.trim()) return
    setScreenerLoading(true); setScreenerRes([])
    try {
      const res = await api.screener(screenerQ.trim(), screenerExch)
      if (res.success) setScreenerRes(res.results || [])
    } catch { /* ignore */ }
    finally { setScreenerLoading(false) }
  }

  // Screener quick-add: pre-fill the add form and switch tab
  const screenerQuickAdd = (sym) => {
    setAddInput(sym); setTab('add')
    setTimeout(async () => {
      setValidating(true); setValidation(null)
      try {
        const res = await api.validateSymbol(sym)
        setValidation(res)
      } finally { setValidating(false) }
    }, 100)
  }

  // ── Bulk import ────────────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      // Accept: symbol,sector per line OR just symbol per line
      const lines = (ev.target.result || '').split('\n').map(l => l.trim()).filter(Boolean)
      const parsed = lines
        .filter(l => !l.toLowerCase().startsWith('symbol'))  // skip header
        .map(l => { const [sym, sec] = l.split(','); return `${(sym||'').trim()},${(sec||'').trim()}` })
        .join('\n')
      setBulkText(parsed)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const handleBulkAdd = async () => {
    if (!bulkText.trim()) return
    // Parse lines: "AAPL" or "AAPL,Technology"
    const lines = bulkText.trim().split('\n').map(l => l.trim()).filter(Boolean)
    const symbols = lines.map(l => {
      const [sym, sec] = l.split(',')
      return { symbol: (sym || '').trim().toUpperCase(), sector: (sec || bulkSector).trim() || bulkSector }
    }).filter(s => s.symbol)

    if (!symbols.length) return
    setBulkLoading(true); setBulkResult(null)
    try {
      const res = await api.bulkAdd(symbols)
      setBulkResult(res)
      if (res.success) { await fetchAll(); setTimeout(fetchAll, 3000) }
    } catch { setBulkResult({ success: false, message: 'Request failed' }) }
    finally { setBulkLoading(false) }
  }

  // ── Validate all ───────────────────────────────────────────────────────
  const handleValidateAll = async () => {
    setValidatingAll(true)
    try { await api.validateAll() }
    catch { /* ignore */ }
    setTimeout(() => { setValidatingAll(false); fetchAll() }, 5000)
  }

  // ── Remove symbol ──────────────────────────────────────────────────────
  const handleRemove = async (symbol) => {
    if (!window.confirm(`Deactivate ${symbol}?\n\nIt will be removed from the engine's collection within ~1 minute (hot-reload). Historical data is preserved.`)) return
    setRemoving(symbol)
    try {
      const res = await api.removeSymbol(symbol)
      if (res.success) await fetchAll()
    } catch { /* ignore */ }
    finally { setRemoving(null) }
  }

  // ── Create watchlist ───────────────────────────────────────────────────
  const handleCreateList = async () => {
    if (!newListName.trim()) return
    setCreatingList(true)
    try {
      const res = await api.createWatchlist(newListName.trim(), newListDesc.trim())
      if (res.success) { setNewListName(''); setNewListDesc(''); await fetchAll() }
    } catch { /* ignore */ }
    finally { setCreatingList(false) }
  }

  // ── TradingView fetch + auto-detect sectors ───────────────────────────
  const handleTvFetch = async () => {
    if (!tvUrl.trim()) return
    setTvFetching(true); setTvPreview(null); setTvResult(null)
    setTvDetectedSectors({}); setTvSectorOverrides({})
    try {
      const res = await api.fetchTradingView(tvUrl.trim())
      if (res.success) {
        setTvPreview(res)
        // Auto-detect sectors for new symbols in the background
        if (res.new && res.new.length > 0) {
          setTvDetecting(true)
          api.detectSectors(res.new)
            .then(d => { if (d.sectors) setTvDetectedSectors(d.sectors) })
            .catch(() => {})
            .finally(() => setTvDetecting(false))
        }
      } else {
        setTvPreview({ error: res.error || 'Unknown error' })
      }
    } catch (e) {
      setTvPreview({ error: e.message || 'Request failed' })
    } finally { setTvFetching(false) }
  }

  const handleTvSync = async () => {
    if (!tvPreview || !tvPreview.symbols || !tvPreview.symbols.length) return
    setTvSyncing(true); setTvResult(null)

    // Build sector map: explicit override > auto-detected > default sector
    const sectorMap = {}
    for (const s of (tvPreview.new || [])) {
      sectorMap[s] = tvSectorOverrides[s] || tvDetectedSectors[s] || tvDefaultSector
    }

    try {
      const res = await api.syncTradingView(tvPreview.symbols, tvDisableMissing, sectorMap)
      setTvResult(res)
      await fetchAll()
      setTimeout(fetchAll, 4000)
    } catch (e) {
      setTvResult({ success: false, message: e.message || 'Sync failed' })
    } finally {
      setTvSyncing(false)
    }
  }

  // ── Tab button helper ──────────────────────────────────────────────────
  const TabBtn = ({ id, label, icon: Icon }) => (
    <button onClick={() => setTab(id)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
      style={{
        ...mono, letterSpacing: '0.06em', fontSize: '0.68rem',
        background: tab === id ? '#1d4ed8' : '#111827',
        color:      tab === id ? '#bfdbfe' : '#64748b',
        border:     tab === id ? '1px solid rgba(59,130,246,0.4)' : '1px solid #374151',
      }}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  )

  return (
    <div className="space-y-5">

      {/* Header row */}
      <div className="flex items-center justify-between">
        <div />
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
          style={{ background: '#111827', color: '#64748b', border: '1px solid #1f2937', ...mono, fontSize: '0.68rem', letterSpacing: '0.06em' }}
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          REFRESH
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Active',    value: activeCount,  icon: List,      color: '#3b82f6' },
          { label: 'Total',     value: symbols.length, icon: BarChart2, color: '#a78bfa' },
          { label: 'Sectors',   value: sectorCount,  icon: TrendingUp, color: '#10b981' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl p-4"
            style={{ background: '#111827', border: '1px solid #1f2937',
                     borderTop: `1.5px solid ${color}`, boxShadow: `0 -1px 8px ${color}33` }}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-600 uppercase"
                  style={{ fontSize: '0.6rem', letterSpacing: '0.12em', fontWeight: 600 }}>
                  {label}
                </p>
                <p className="font-bold mt-1.5 tabular-nums"
                  style={{ color, ...mono, fontSize: '1.4rem' }}>
                  {loading ? '—' : value}
                </p>
              </div>
              <Icon className="h-4 w-4 mt-0.5" style={{ color }} />
            </div>
          </div>
        ))}
      </div>

      {/* Action panel with tabs */}
      <div className="rounded-xl border border-gray-800" style={{ background: '#111827' }}>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 px-5 pt-5 pb-0 border-b border-gray-800 pb-4">
          <TabBtn id="add"         label="Add Symbol"      icon={Plus}       />
          <TabBtn id="screener"    label="Screener"        icon={Search}     />
          <TabBtn id="bulk"        label="Bulk Import"     icon={Upload}     />
          <TabBtn id="tradingview" label="TradingView"     icon={Link}       />
          <TabBtn id="lists"       label="Manage Lists"    icon={FolderPlus} />
        </div>

        <div className="p-5">

          {/* ── Tab: Add Symbol ───────────────────────────────────────── */}
          {tab === 'add' && (
            <div>
              <p className="text-slate-700 mb-4" style={{ fontSize: '0.65rem', ...mono }}>
                Validates against Alpaca first. Triggers automatic historical backfill
                (1yr daily + 90d 1-min). Engine picks up the new symbol within ~1 min
                via hot-reload — no restart needed.
              </p>

              <div className="flex flex-wrap gap-3 items-end">
                {/* Ticker */}
                <div className="flex-1 min-w-[130px]">
                  <p className="text-slate-500 mb-1.5" style={{ fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>Ticker</p>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-600" />
                    <input type="text" value={addInput}
                      onChange={e => { setAddInput(e.target.value.toUpperCase()); setValidation(null); setAddResult(null) }}
                      onKeyDown={e => e.key === 'Enter' && handleValidate()}
                      placeholder="AAPL" maxLength={10}
                      className="w-full pl-8 pr-3 py-2 rounded-lg text-slate-200 focus:outline-none placeholder-slate-700"
                      style={{ background: '#0f172a', border: '1px solid #374151', ...mono, letterSpacing: '0.08em', fontSize: '0.85rem' }} />
                  </div>
                </div>

                {/* Sector */}
                <div className="flex-1 min-w-[150px]">
                  <p className="text-slate-500 mb-1.5" style={{ fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>Sector</p>
                  <select value={addSector} onChange={e => setAddSector(e.target.value)}
                    className="w-full rounded-lg py-2" style={darkSelect}>
                    {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {/* Watchlist */}
                <div className="flex-1 min-w-[150px]">
                  <p className="text-slate-500 mb-1.5" style={{ fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>Watchlist</p>
                  <select value={addWatchlist} onChange={e => setAddWatchlist(e.target.value)}
                    className="w-full rounded-lg py-2" style={darkSelect}>
                    <option value="main_watchlist">main_watchlist</option>
                    {watchlists.map(w => <option key={w.name} value={w.name}>{w.name}</option>)}
                  </select>
                </div>

                {/* Buttons */}
                <button onClick={handleValidate} disabled={!addInput.trim() || validating}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40"
                  style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #374151', ...mono, fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                  {validating ? <RefreshCw className="h-3.5 w-3.5 animate-spin inline" /> : 'VALIDATE'}
                </button>

                <button onClick={handleAdd} disabled={!validation?.tradeable || adding || validation?.already_in_watchlist}
                  className="px-5 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40"
                  style={{ background: '#15803d', color: '#bbf7d0', border: '1px solid rgba(34,197,94,0.35)', ...mono, fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                  {adding ? <RefreshCw className="h-3.5 w-3.5 animate-spin inline" /> : '+ ADD'}
                </button>
              </div>

              {/* Validation result */}
              {validation && (
                <div className="mt-4 rounded-lg p-4"
                  style={{ background: validation.tradeable ? 'rgba(16,185,129,0.06)' : 'rgba(244,63,94,0.06)',
                           border: `1px solid ${validation.tradeable ? 'rgba(16,185,129,0.2)' : 'rgba(244,63,94,0.2)'}` }}>
                  {!validation.success ? (
                    <p className="text-rose-400 text-sm flex items-center gap-2">
                      <XCircle className="h-4 w-4" /> {validation.error}
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        {validation.tradeable ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-rose-400" />}
                        <span className="font-semibold"
                          style={{ color: validation.tradeable ? '#10b981' : '#f43f5e', ...mono, fontSize: '0.9rem' }}>
                          {validation.symbol}
                        </span>
                        {validation.name && validation.name !== validation.symbol && (
                          <span className="text-slate-500 text-sm">— {validation.name}</span>
                        )}
                        {validation.already_in_watchlist && (
                          <span className="ml-auto text-amber-400" style={{ fontSize: '0.68rem', ...mono }}>Already active</span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {[
                          ['Exchange',    validation.exchange],
                          ['Price',       validation.current_price ? `$${validation.current_price}` : '—'],
                          ['Class',       validation.asset_class],
                          ['Tradeable',   validation.tradeable ? 'YES' : 'NO'],
                          ['Fractionable', validation.fractionable ? 'YES' : 'NO'],
                          ['Shortable',   validation.shortable ? 'YES' : 'NO'],
                        ].map(([k, v]) => (
                          <div key={k} className="rounded p-2" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
                            <p className="text-slate-600 uppercase" style={{ ...mono, fontSize: '0.58rem', letterSpacing: '0.08em' }}>{k}</p>
                            <p className="text-slate-300 font-medium mt-0.5" style={{ ...mono, fontSize: '0.72rem' }}>{v || '—'}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {addResult && (
                <div className="mt-3 rounded-lg px-4 py-3 text-sm"
                  style={{ background: addResult.success ? 'rgba(16,185,129,0.08)' : 'rgba(244,63,94,0.08)',
                           border:     addResult.success ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(244,63,94,0.25)',
                           color:      addResult.success ? '#10b981' : '#f43f5e' }}>
                  {addResult.success
                    ? <>{addResult.message}<br/>
                        <span className="text-slate-500" style={{ fontSize: '0.65rem', ...mono }}>
                          Hot-reload active — engine picks up the new symbol within ~1 minute.
                        </span></>
                    : addResult.error}
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Screener ─────────────────────────────────────────── */}
          {tab === 'screener' && (
            <div>
              <p className="text-slate-700 mb-4" style={{ fontSize: '0.65rem', ...mono }}>
                Search Alpaca's full US equity universe. Click a result to pre-fill the Add Symbol form.
              </p>
              <div className="flex flex-wrap gap-3 items-end mb-4">
                <div className="flex-1 min-w-[140px]">
                  <p className="text-slate-500 mb-1.5" style={{ fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>Search</p>
                  <input type="text" value={screenerQ}
                    onChange={e => setScreenerQ(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === 'Enter' && handleScreener()}
                    placeholder="AAPL or AAPL…" maxLength={10}
                    className="w-full rounded-lg text-slate-200 focus:outline-none placeholder-slate-700"
                    style={{ background: '#0f172a', border: '1px solid #374151', padding: '0.5rem 0.75rem', ...mono, fontSize: '0.82rem' }} />
                </div>
                <div>
                  <p className="text-slate-500 mb-1.5" style={{ fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>Exchange</p>
                  <select value={screenerExch} onChange={e => setScreenerExch(e.target.value)}
                    className="rounded-lg" style={darkSelect}>
                    <option value="">All</option>
                    {['NASDAQ','NYSE','AMEX','OTC'].map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <button onClick={handleScreener} disabled={!screenerQ.trim() || screenerLoading}
                  className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40"
                  style={{ background: '#1d4ed8', color: '#bfdbfe', border: '1px solid rgba(59,130,246,0.4)', ...mono, fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                  {screenerLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin inline" /> : 'SEARCH'}
                </button>
              </div>

              {screenerRes.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-gray-800">
                  <table className="w-full text-sm">
                    <thead style={{ background: '#0f172a' }}>
                      <tr>
                        {['Symbol', 'Name', 'Exchange', 'Fractionable', 'Shortable', ''].map((h, i) => (
                          <th key={h} className={`px-3 py-2 ${i >= 3 ? 'text-center' : 'text-left'}`}
                            style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.1em', color: '#4b5563', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {screenerRes.map((r, i) => (
                        <tr key={r.symbol} style={{ background: i % 2 === 0 ? '#0f172a' : '#111827', opacity: r.in_watchlist ? 0.5 : 1 }}
                          className="hover:bg-gray-800 transition-colors">
                          <td className="px-3 py-2 font-semibold text-slate-200" style={mono}>{r.symbol}</td>
                          <td className="px-3 py-2 text-slate-500 truncate max-w-[160px]" style={{ fontSize: '0.72rem' }}>{r.name}</td>
                          <td className="px-3 py-2 text-slate-500" style={{ fontSize: '0.72rem', ...mono }}>{r.exchange}</td>
                          <td className="px-3 py-2 text-center">{r.fractionable ? <CheckCircle className="h-3.5 w-3.5 text-emerald-400 mx-auto" /> : <span className="text-slate-700">—</span>}</td>
                          <td className="px-3 py-2 text-center">{r.shortable ? <CheckCircle className="h-3.5 w-3.5 text-emerald-400 mx-auto" /> : <span className="text-slate-700">—</span>}</td>
                          <td className="px-3 py-2 text-right">
                            {r.in_watchlist ? (
                              <span className="text-slate-600" style={{ fontSize: '0.62rem', ...mono }}>In watchlist</span>
                            ) : (
                              <button onClick={() => screenerQuickAdd(r.symbol)}
                                className="px-2.5 py-1 rounded text-xs font-medium"
                                style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)', ...mono, fontSize: '0.62rem' }}>
                                + Add
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {screenerRes.length === 0 && !screenerLoading && screenerQ && (
                <p className="text-slate-700" style={{ ...mono, fontSize: '0.7rem' }}>No results. Try a shorter prefix (e.g. "AAP" for AAPL).</p>
              )}
            </div>
          )}

          {/* ── Tab: Bulk Import ──────────────────────────────────────── */}
          {tab === 'bulk' && (
            <div>
              <p className="text-slate-700 mb-4" style={{ fontSize: '0.65rem', ...mono }}>
                Paste or upload a list of tickers. Format: one per line, optionally with sector
                (e.g. AAPL,Technology). Each symbol is validated against Alpaca before adding.
                Up to 50 symbols per batch.
              </p>

              {/* File upload */}
              <div className="flex gap-3 mb-3 items-end">
                <div>
                  <p className="text-slate-500 mb-1.5" style={{ fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>Default Sector (if CSV has no sector)</p>
                  <select value={bulkSector} onChange={e => setBulkSector(e.target.value)}
                    className="rounded-lg" style={darkSelect}>
                    {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <button onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #374151', ...mono, fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                  <Upload className="h-3.5 w-3.5" /> UPLOAD CSV
                </button>
                <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFileUpload} />
              </div>

              <textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
                placeholder={'AAPL,Technology\nMSFT\nTSLA,Consumer Discretionary\nNVDA'}
                rows={8}
                className="w-full rounded-lg p-3 focus:outline-none resize-y"
                style={{ background: '#0f172a', border: '1px solid #374151', color: '#94a3b8', ...mono, fontSize: '0.72rem', letterSpacing: '0.03em' }} />

              <div className="flex items-center gap-3 mt-3">
                <button onClick={handleBulkAdd} disabled={!bulkText.trim() || bulkLoading}
                  className="px-5 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40"
                  style={{ background: '#15803d', color: '#bbf7d0', border: '1px solid rgba(34,197,94,0.35)', ...mono, fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                  {bulkLoading ? <><RefreshCw className="h-3.5 w-3.5 animate-spin inline mr-2" />ADDING…</> : '+ ADD ALL VALID'}
                </button>
                <span className="text-slate-700" style={{ fontSize: '0.65rem', ...mono }}>
                  {bulkText.trim().split('\n').filter(Boolean).length} symbols in list
                </span>
              </div>

              {bulkResult && (
                <div className="mt-4 rounded-lg p-4"
                  style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)' }}>
                  <p className="text-slate-300 font-medium text-sm mb-3">{bulkResult.message}</p>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[['Added', bulkResult.summary?.added, '#10b981'],
                      ['Skipped', bulkResult.summary?.skipped, '#f59e0b'],
                      ['Errors', bulkResult.summary?.errors, '#f43f5e']].map(([l, v, c]) => (
                      <div key={l} className="rounded p-2 text-center" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
                        <p style={{ color: c, ...mono, fontSize: '1.2rem', fontWeight: 700 }}>{v ?? 0}</p>
                        <p className="text-slate-600" style={{ fontSize: '0.62rem' }}>{l}</p>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {(bulkResult.results || []).map((r, i) => (
                      <p key={i} style={{ fontSize: '0.65rem', ...mono,
                        color: r.status === 'added' ? '#10b981' : r.status === 'skipped' ? '#f59e0b' : '#f43f5e' }}>
                        {r.symbol}: {r.status === 'added' ? `Added (${r.sector})` : r.reason}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: TradingView Sync ─────────────────────────────────── */}
          {tab === 'tradingview' && (
            <div>
              <p className="text-slate-700 mb-4" style={{ fontSize: '0.65rem', ...mono }}>
                Paste the URL of a public TradingView watchlist. FETCH previews the diff —
                new symbols to add and symbols no longer in the TV list. SYNC commits the
                changes: new tickers are backfilled and begin trading immediately; missing
                tickers (if enabled) are flagged for exit on the next scanner cycle.
              </p>

              {/* URL + default sector + fetch */}
              <div className="flex flex-wrap gap-3 items-end mb-4">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-slate-500 mb-1.5" style={{ fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>
                    TradingView Watchlist URL
                  </p>
                  <input
                    value={tvUrl}
                    onChange={e => { setTvUrl(e.target.value); setTvPreview(null); setTvResult(null) }}
                    placeholder="https://www.tradingview.com/watchlists/197983740/"
                    className="w-full rounded-lg px-3 py-2 focus:outline-none"
                    style={{ background: '#0f172a', border: '1px solid #374151', color: '#94a3b8', ...mono, fontSize: '0.72rem' }}
                  />
                </div>
                <div>
                  <p className="text-slate-500 mb-1.5" style={{ fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>
                    Default Sector
                  </p>
                  <select value={tvDefaultSector} onChange={e => setTvDefaultSector(e.target.value)}
                    className="rounded-lg" style={darkSelect}>
                    {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <button onClick={handleTvFetch} disabled={!tvUrl.trim() || tvFetching}
                  className="px-5 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40 flex items-center gap-2"
                  style={{ background: '#1e3a5f', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.35)', ...mono, fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                  {tvFetching
                    ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" />FETCHING…</>
                    : <><Link className="h-3.5 w-3.5" />FETCH</>}
                </button>
              </div>

              {/* Error state */}
              {tvPreview?.error && (
                <div className="rounded-lg p-3 mb-4" style={{ background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.25)' }}>
                  <p className="text-rose-400" style={{ fontSize: '0.72rem', ...mono }}>{tvPreview.error}</p>
                </div>
              )}

              {/* Preview */}
              {tvPreview && !tvPreview.error && (
                <div>
                  {/* Disable-missing toggle + sync button row */}
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                      <p className="text-slate-400 font-medium mb-1" style={{ fontSize: '0.72rem', ...mono }}>
                        {tvPreview.watchlist_name && (
                          <span className="text-blue-400 mr-2">{tvPreview.watchlist_name}</span>
                        )}
                        <span className="text-emerald-400">{tvPreview.new_count} new</span>
                        <span className="text-slate-600 mx-2">/</span>
                        <span className="text-slate-500">{tvPreview.already_count} active</span>
                        {(tvPreview.to_disable_count || 0) > 0 && (
                          <>
                            <span className="text-slate-600 mx-2">/</span>
                            <span className="text-amber-500">{tvPreview.to_disable_count} not in TV list</span>
                          </>
                        )}
                      </p>
                      {/* Disable-missing toggle — only show if there are symbols to disable */}
                      {(tvPreview.to_disable_count || 0) > 0 && (
                        <label className="flex items-center gap-2 cursor-pointer mt-1">
                          <div
                            onClick={() => setTvDisableMissing(v => !v)}
                            className="relative inline-flex h-4 w-7 items-center rounded-full transition-colors"
                            style={{ background: tvDisableMissing ? '#b45309' : '#374151' }}>
                            <span className="inline-block h-3 w-3 transform rounded-full bg-white transition-transform"
                              style={{ transform: tvDisableMissing ? 'translateX(14px)' : 'translateX(2px)' }} />
                          </div>
                          <span style={{ fontSize: '0.65rem', ...mono, color: tvDisableMissing ? '#fbbf24' : '#64748b' }}>
                            Flag {tvPreview.to_disable_count} missing symbols for exit
                          </span>
                        </label>
                      )}
                    </div>
                    <button
                      onClick={handleTvSync}
                      disabled={(!tvPreview.new_count && !tvDisableMissing) || tvSyncing}
                      className="px-5 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40 flex items-center gap-2"
                      style={{ background: '#15803d', color: '#bbf7d0', border: '1px solid rgba(34,197,94,0.35)', ...mono, fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                      {tvSyncing
                        ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" />SYNCING…</>
                        : <>
                            SYNC
                            {tvPreview.new_count > 0 && ` +${tvPreview.new_count}`}
                            {tvDisableMissing && (tvPreview.to_disable_count || 0) > 0 && ` / EXIT ${tvPreview.to_disable_count}`}
                          </>}
                    </button>
                  </div>

                  {/* Disable-missing warning box */}
                  {tvDisableMissing && (tvPreview.to_disable_count || 0) > 0 && (
                    <div className="rounded-lg p-3 mb-4" style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.3)' }}>
                      <p className="text-amber-400 font-medium mb-2" style={{ fontSize: '0.68rem', ...mono, letterSpacing: '0.06em' }}>
                        PENDING EXIT — these symbols will be flagged for immediate position close
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {(tvPreview.to_disable || []).map(s => (
                          <span key={s} className="text-amber-600" style={{ fontSize: '0.68rem', ...mono }}>{s}</span>
                        ))}
                      </div>
                      <p className="text-slate-600 mt-2" style={{ fontSize: '0.62rem', ...mono }}>
                        Open positions will be closed at market on the next scanner cycle (~5 min).
                        No new buys will be placed. Data collection stops after exit.
                      </p>
                    </div>
                  )}

                  {/* New symbols with per-symbol sector override */}
                  {tvPreview.new_count > 0 && (
                    <div className="mb-4">
                      <p className="text-emerald-600 mb-2 flex items-center gap-2" style={{ fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', ...mono }}>
                        New symbols — sector auto-detected
                        {tvDetecting && <><RefreshCw className="h-2.5 w-2.5 animate-spin inline" /> detecting…</>}
                      </p>
                      <div className="rounded-lg border border-gray-800 overflow-hidden">
                        <div className="max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead style={{ background: '#0f172a', position: 'sticky', top: 0 }}>
                              <tr>
                                <th className="px-3 py-2 text-left" style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.1em', color: '#4b5563', textTransform: 'uppercase' }}>Symbol</th>
                                <th className="px-3 py-2 text-left" style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.1em', color: '#4b5563', textTransform: 'uppercase' }}>Sector</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(tvPreview.new || []).map((s, i) => {
                                const autoSector    = tvDetectedSectors[s]
                                const currentVal    = tvSectorOverrides[s] || autoSector || tvDefaultSector
                                const isOverridden  = !!tvSectorOverrides[s]
                                const isAutoDetected = !isOverridden && !!autoSector
                                return (
                                  <tr key={s} style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}>
                                    <td className="px-3 py-1.5" style={{ ...mono, fontSize: '0.78rem' }}>
                                      <span className="font-semibold text-emerald-400">{s}</span>
                                    </td>
                                    <td className="px-3 py-1.5">
                                      <div className="flex items-center gap-1.5">
                                        <select
                                          value={currentVal}
                                          onChange={e => setTvSectorOverrides(prev => ({ ...prev, [s]: e.target.value }))}
                                          style={{
                                            ...darkSelect,
                                            padding: '0.2rem 0.5rem',
                                            fontSize: '0.68rem',
                                            borderColor: isAutoDetected ? 'rgba(59,130,246,0.4)' : isOverridden ? 'rgba(245,158,11,0.4)' : '#374151',
                                          }}>
                                          {SECTORS.map(sec => <option key={sec} value={sec}>{sec}</option>)}
                                        </select>
                                        {isAutoDetected && (
                                          <span style={{ fontSize: '0.55rem', color: '#3b82f6', ...mono }}>AUTO</span>
                                        )}
                                        {isOverridden && (
                                          <button onClick={() => setTvSectorOverrides(prev => { const n = {...prev}; delete n[s]; return n })}
                                            style={{ fontSize: '0.55rem', color: '#64748b', cursor: 'pointer', background: 'none', border: 'none' }}
                                            title="Reset to auto-detected">↺</button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Already-present symbols (collapsed) */}
                  {tvPreview.already_count > 0 && (
                    <details className="rounded-lg border border-gray-800 overflow-hidden">
                      <summary className="px-3 py-2 cursor-pointer text-slate-600"
                        style={{ background: '#0f172a', fontSize: '0.65rem', ...mono, letterSpacing: '0.06em' }}>
                        Already active in watchlist ({tvPreview.already_count})
                      </summary>
                      <div className="px-3 py-2 flex flex-wrap gap-x-4 gap-y-1">
                        {(tvPreview.already || []).map(s => (
                          <span key={s} className="text-slate-600" style={{ fontSize: '0.68rem', ...mono }}>{s}</span>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Sync result */}
              {tvResult && (
                <div className="mt-4 rounded-lg p-4"
                  style={{ background: tvResult.success ? 'rgba(16,185,129,0.06)' : 'rgba(244,63,94,0.06)',
                           border: `1px solid ${tvResult.success ? 'rgba(16,185,129,0.2)' : 'rgba(244,63,94,0.2)'}` }}>
                  <p className="font-medium text-sm mb-3" style={{ color: tvResult.success ? '#d1fae5' : '#fecdd3' }}>
                    {tvResult.success ? 'Sync complete' : 'Sync failed'}
                  </p>
                  {tvResult.success && (
                    <>
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {[
                          ['Added',      tvResult.summary?.added,    '#10b981'],
                          ['Already',    tvResult.summary?.already,  '#64748b'],
                          ['Exiting',    tvResult.summary?.disabled, '#f59e0b'],
                          ['DB Errors',  tvResult.summary?.failed,   '#f43f5e'],
                        ].map(([l, v, c]) => (
                          <div key={l} className="rounded p-2 text-center" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
                            <p style={{ color: c, ...mono, fontSize: '1.1rem', fontWeight: 700 }}>{v ?? 0}</p>
                            <p className="text-slate-600" style={{ fontSize: '0.62rem' }}>{l}</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-slate-500" style={{ fontSize: '0.65rem', ...mono }}>{tvResult.message}</p>
                      <p className="text-slate-700 mt-1" style={{ fontSize: '0.62rem', ...mono }}>{tvResult.note}</p>
                      {/* Show data-only symbols (added but not Alpaca-tradeable) */}
                      {(tvResult.added || []).some(a => !a.alpaca_tradeable) && (
                        <details className="mt-3 rounded-lg border border-gray-800 overflow-hidden">
                          <summary className="px-3 py-2 cursor-pointer text-slate-600"
                            style={{ background: '#0f172a', fontSize: '0.65rem', ...mono, letterSpacing: '0.06em' }}>
                            Data-only symbols ({(tvResult.added || []).filter(a => !a.alpaca_tradeable).length}) — added for data collection, not Alpaca-tradeable
                          </summary>
                          <div className="px-3 py-2 flex flex-wrap gap-x-3 gap-y-1">
                            {(tvResult.added || []).filter(a => !a.alpaca_tradeable).map(a => (
                              <span key={a.symbol} className="text-slate-600" style={{ fontSize: '0.68rem', ...mono }}
                                title={a.note}>{a.symbol}</span>
                            ))}
                          </div>
                        </details>
                      )}
                      {/* Show DB errors if any */}
                      {(tvResult.failed || []).length > 0 && (
                        <details className="mt-2 rounded-lg border border-gray-800 overflow-hidden">
                          <summary className="px-3 py-2 cursor-pointer text-rose-700"
                            style={{ background: '#0f172a', fontSize: '0.65rem', ...mono, letterSpacing: '0.06em' }}>
                            Errors ({(tvResult.failed || []).length})
                          </summary>
                          <div className="px-3 py-2 space-y-1">
                            {(tvResult.failed || []).map(f => (
                              <p key={f.symbol} className="text-rose-600" style={{ fontSize: '0.65rem', ...mono }}>
                                {f.symbol}: {f.reason}
                              </p>
                            ))}
                          </div>
                        </details>
                      )}
                    </>
                  )}
                  {!tvResult.success && (
                    <p className="text-rose-400" style={{ fontSize: '0.72rem', ...mono }}>{tvResult.message}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Manage Lists ─────────────────────────────────────── */}
          {tab === 'lists' && (
            <div className="space-y-5">

              {/* Existing watchlists */}
              <div>
                <p className="text-slate-500 mb-3" style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>
                  Existing Watchlists
                </p>
                {watchlists.length === 0 ? (
                  <p className="text-slate-700" style={{ fontSize: '0.72rem', ...mono }}>No named watchlists yet.</p>
                ) : (
                  <div className="space-y-2">
                    {watchlists.map(w => (
                      <div key={w.name} className="flex items-center justify-between rounded-lg p-3"
                        style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
                        <div className="flex items-center gap-3">
                          <Folder className="h-4 w-4 text-slate-600" />
                          <div>
                            <p className="text-slate-300 font-medium text-sm" style={mono}>{w.name}</p>
                            {w.description && <p className="text-slate-600 mt-0.5" style={{ fontSize: '0.65rem' }}>{w.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-slate-500 tabular-nums" style={{ fontSize: '0.68rem', ...mono }}>
                            {w.active_symbols} / {w.total_symbols} active
                          </span>
                          {w.updated_at && (
                            <span className="text-slate-700" style={{ fontSize: '0.62rem', ...mono }}>
                              updated {timeAgo(w.updated_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Create new watchlist */}
              <div>
                <p className="text-slate-500 mb-3" style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...mono }}>
                  Create New Watchlist
                </p>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[160px]">
                    <p className="text-slate-600 mb-1.5" style={{ fontSize: '0.62rem', ...mono }}>Name</p>
                    <input type="text" value={newListName} onChange={e => setNewListName(e.target.value)}
                      placeholder="growth_stocks" maxLength={100}
                      className="w-full rounded-lg focus:outline-none"
                      style={{ ...darkInput, color: '#94a3b8' }} />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-slate-600 mb-1.5" style={{ fontSize: '0.62rem', ...mono }}>Description (optional)</p>
                    <input type="text" value={newListDesc} onChange={e => setNewListDesc(e.target.value)}
                      placeholder="High-growth tech and biotech"
                      className="w-full rounded-lg focus:outline-none"
                      style={{ ...darkInput, color: '#94a3b8' }} />
                  </div>
                  <button onClick={handleCreateList} disabled={!newListName.trim() || creatingList}
                    className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40"
                    style={{ background: '#1d4ed8', color: '#bfdbfe', border: '1px solid rgba(59,130,246,0.4)', ...mono, fontSize: '0.72rem' }}>
                    {creatingList ? <RefreshCw className="h-3.5 w-3.5 animate-spin inline" /> : 'CREATE'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Symbol table */}
      <div className="rounded-xl border border-gray-800 overflow-hidden" style={{ background: '#111827' }}>
        <div className="px-5 py-4 flex flex-wrap items-center gap-3" style={{ borderBottom: '1px solid #1f2937' }}>
          <h3 className="section-header" style={{ marginBottom: 0, flex: '1 1 auto' }}>
            Watchlist Symbols
          </h3>

          {/* Filter controls */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-600" />
            <input type="text" value={filterText} onChange={e => setFilterText(e.target.value)}
              placeholder="Filter…"
              className="pl-7 pr-3 py-1.5 rounded-lg text-slate-300 focus:outline-none"
              style={{ background: '#0f172a', border: '1px solid #374151', ...mono, fontSize: '0.72rem', width: '140px' }} />
          </div>

          {/* Watchlist filter */}
          <select value={filterList} onChange={e => setFilterList(e.target.value)}
            className="rounded-lg py-1.5" style={{ ...darkSelect, width: '130px' }}>
            <option value="all">All lists</option>
            {[...new Set(symbols.map(s => s.watchlist_name))].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>

          {/* Show inactive toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <div onClick={() => setShowInactive(v => !v)}
              className="relative inline-flex h-4 w-7 items-center rounded-full transition-all cursor-pointer"
              style={{ background: showInactive ? '#1d4ed8' : '#1f2937', border: showInactive ? '1px solid rgba(59,130,246,0.4)' : '1px solid #374151' }}>
              <span className="inline-block h-3 w-3 rounded-full transition-transform"
                style={{ background: showInactive ? '#93c5fd' : '#475569',
                         transform: showInactive ? 'translateX(14px)' : 'translateX(2px)' }} />
            </div>
            <span className="text-slate-600" style={{ fontSize: '0.62rem', ...mono }}>Inactive</span>
          </label>

          {/* Validate All */}
          <button onClick={handleValidateAll} disabled={validatingAll}
            className="flex items-center gap-1.5 border border-gray-700 rounded-lg px-3 py-1.5 text-slate-500 hover:text-slate-300 hover:bg-gray-800 transition-colors disabled:opacity-40"
            style={{ ...mono, fontSize: '0.68rem' }}>
            <ShieldCheck className={`h-3.5 w-3.5 ${validatingAll ? 'animate-pulse' : ''}`} />
            {validatingAll ? 'VALIDATING…' : 'VALIDATE ALL'}
          </button>

          <button onClick={fetchAll} disabled={loading}
            className="flex items-center gap-1.5 border border-gray-700 rounded-lg px-3 py-1.5 text-slate-500 hover:text-slate-300 hover:bg-gray-800 transition-colors"
            style={{ ...mono, fontSize: '0.68rem' }}>
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && <div className="px-5 py-3 text-rose-400" style={{ fontSize: '0.75rem', borderBottom: '1px solid #1f2937' }}>{error}</div>}

        {loading ? (
          <div className="p-10 text-center text-slate-700">
            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
            <p style={{ ...mono, fontSize: '0.7rem' }}>LOADING…</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: '#0f172a' }}>
                <tr>
                  <SortHeader label="Symbol"     field="symbol"       sort={sort} onSort={handleSort} />
                  <SortHeader label="Sector"     field="sector"       sort={sort} onSort={handleSort} />
                  <SortHeader label="List"       field="watchlist_name" sort={sort} onSort={handleSort} />
                  <th className="px-4 py-3 text-left" style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.1em', color: '#4b5563', fontWeight: 600, textTransform: 'uppercase' }}>Status</th>
                  <SortHeader label="Bars Today" field="bars_today"   sort={sort} onSort={handleSort} right />
                  <SortHeader label="Signals 7d" field="signals_7d"   sort={sort} onSort={handleSort} right />
                  <SortHeader label="Win Rate"   field="win_rate"     sort={sort} onSort={handleSort} right />
                  <SortHeader label="Trades"     field="number_of_trades" sort={sort} onSort={handleSort} right />
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={10} className="px-5 py-10 text-center text-slate-700" style={{ ...mono, fontSize: '0.7rem' }}>
                    {filterText ? 'No symbols match filter' : 'No symbols in watchlist'}
                  </td></tr>
                ) : filtered.map((s, i) => (
                  <tr key={s.symbol}
                    style={{
                      background: s.pending_removal
                        ? 'rgba(180,83,9,0.07)'
                        : i % 2 === 0 ? '#0f172a' : '#111827',
                      opacity: s.is_active ? 1 : 0.4,
                      borderLeft: s.pending_removal ? '2px solid rgba(245,158,11,0.5)' : undefined,
                    }}
                    className="hover:bg-gray-800 transition-colors">

                    <td className="px-4 py-2.5 font-semibold" style={{ ...mono, color: s.pending_removal ? '#fbbf24' : '#e2e8f0' }}>
                      {s.symbol}
                      {!s.is_active && <span className="ml-1.5 text-slate-700" style={{ fontSize: '0.58rem' }}>OFF</span>}
                      {s.pending_removal && (
                        <span className="ml-1.5 px-1 py-0.5 rounded text-amber-800 bg-amber-900/30"
                          style={{ fontSize: '0.55rem', letterSpacing: '0.08em', border: '1px solid rgba(180,83,9,0.4)' }}>
                          EXITING
                        </span>
                      )}
                      {s.is_active && (s.buy_enabled === false || s.trade_enabled === false) && (
                        <span className="ml-1.5 px-1 py-0.5 rounded text-orange-300 bg-orange-900/30"
                          style={{ fontSize: '0.55rem', letterSpacing: '0.08em', border: '1px solid rgba(249,115,22,0.4)' }}
                          title={s.buy_disabled_by ? `Disabled by: ${s.buy_disabled_by}` : 'No new buys — existing positions still exit normally'}>
                          SELL-ONLY
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-2.5 text-slate-500" style={{ fontSize: '0.72rem' }}>{s.sector}</td>

                    <td className="px-4 py-2.5 text-slate-600" style={{ fontSize: '0.65rem', ...mono }}>{s.watchlist_name}</td>

                    <td className="px-4 py-2.5">
                      {s.validation_status === 'valid' ? (
                        <span className="text-emerald-400" style={{ fontSize: '0.62rem', ...mono }}>VALID</span>
                      ) : s.validation_status === 'invalid' ? (
                        <span className="text-rose-400" style={{ fontSize: '0.62rem', ...mono }}>INVALID</span>
                      ) : s.validation_status === 'error' ? (
                        <span className="text-amber-400" style={{ fontSize: '0.62rem', ...mono }}>ERR</span>
                      ) : (
                        <span className="text-slate-700" style={{ fontSize: '0.62rem', ...mono }}>—</span>
                      )}
                    </td>

                    <td className="px-4 py-2.5 text-right text-slate-400 tabular-nums" style={{ ...mono, fontSize: '0.72rem' }}>
                      {fmtShort(s.bars_today)}
                    </td>

                    <td className="px-4 py-2.5 text-right text-slate-400 tabular-nums" style={{ ...mono, fontSize: '0.72rem' }}>
                      {s.signals_7d || 0}
                    </td>

                    <td className="px-4 py-2.5 text-right tabular-nums"
                      style={{ ...mono, fontSize: '0.72rem',
                               color: s.win_rate > 0.6 ? '#10b981' : s.win_rate > 0 ? '#f59e0b' : '#64748b' }}>
                      {s.win_rate > 0 ? `${(s.win_rate * 100).toFixed(0)}%` : '—'}
                    </td>

                    <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums" style={{ ...mono, fontSize: '0.72rem' }}>
                      {s.number_of_trades || 0}
                    </td>

                    <td className="px-4 py-2.5 text-right">
                      {s.is_active && (
                        <button onClick={() => handleRemove(s.symbol)} disabled={removing === s.symbol}
                          className="p-1.5 rounded hover:bg-rose-900/30 text-slate-700 hover:text-rose-400 transition-colors disabled:opacity-40"
                          title={`Deactivate ${s.symbol}`}>
                          {removing === s.symbol
                            ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2    className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-5 py-3" style={{ borderTop: '1px solid #1f2937' }}>
          <p className="text-slate-700" style={{ fontSize: '0.6rem', ...mono }}>
            {filtered.length} of {symbols.length} symbols shown
            · Auto-refreshes every 5 min
            · Hot-reload: engine picks up changes within ~1 min (no restart needed)
          </p>
        </div>
      </div>
    </div>
  )
}
